import { useConfirm } from "@/contexts/ConfirmDialogContext";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/utils";
import {
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@a7y/tailwind-catalyst";
import { useMutation } from "@tanstack/react-query";
import { Check, Copy, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { Section } from "../shared";
import type { AffiliateAdminLink, AffiliateLink } from "../types";

type StatusMessage = { type: "success" | "error"; text: string };

export function LinksTab({
  affiliateId,
  links,
  onChange,
}: {
  affiliateId: string;
  links: AffiliateAdminLink[];
  onChange: () => void;
}) {
  const confirm = useConfirm();
  const [statusMessage, setStatusMessage] = useState<StatusMessage | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopy = (link: AffiliateAdminLink) => {
    navigator.clipboard.writeText(link.referralUrl);
    setCopiedId(link.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const showStatus = (type: "success" | "error", text: string) => {
    setStatusMessage({ type, text });
    setTimeout(() => setStatusMessage(null), 5000);
  };

  const createMutation = useMutation({
    mutationFn: () => api.affiliates.createLink(affiliateId),
    onSuccess: (res) => {
      if (res.ok) {
        showStatus("success", `Created link token ${res.link.token}`);
        onChange();
      } else {
        // 409 duplicate token — surface distinctly rather than as a hard error.
        showStatus("error", `Duplicate token: ${res.message}`);
      }
    },
    onError: (err: Error) => showStatus("error", `Create failed: ${err.message}`),
  });

  const archiveMutation = useMutation({
    mutationFn: (linkId: string) => api.affiliates.archiveLink(affiliateId, linkId),
    onSuccess: () => {
      showStatus("success", "Link archived");
      onChange();
    },
    onError: (err: Error) => showStatus("error", `Archive failed: ${err.message}`),
  });

  const handleArchive = async (link: AffiliateLink) => {
    const ok = await confirm({
      title: `Archive link ${link.token}?`,
      description:
        "Archiving stops this token from attributing new visitors. Existing referrals are unaffected.",
      confirmLabel: "Archive",
      confirmColor: "red",
    });
    if (ok) archiveMutation.mutate(link.id);
  };

  return (
    <Section
      title={`Tracking Links (${links.length})`}
      actions={
        <Button
          color="blue"
          disabled={createMutation.isPending}
          onClick={() => createMutation.mutate()}
        >
          <Plus data-slot="icon" />
          {createMutation.isPending ? "Creating..." : "New link"}
        </Button>
      }
    >
      {statusMessage && (
        <div
          className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${
            statusMessage.type === "success"
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
          }`}
        >
          {statusMessage.text}
        </div>
      )}

      <Table dense striped>
        <TableHead>
          <TableRow>
            <TableHeader>Token</TableHeader>
            <TableHeader>Referral URL</TableHeader>
            <TableHeader>Created</TableHeader>
            <TableHeader className="text-right">Actions</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {links.length === 0 ? (
            <TableRow>
              <TableCell colSpan={4} className="py-6 text-center">
                <Text className="text-sm text-zinc-500">No tracking links yet</Text>
              </TableCell>
            </TableRow>
          ) : (
            links.map((link) => (
              <TableRow key={link.id}>
                <TableCell className="font-mono text-sm">{link.token}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <span className="font-mono text-sm break-all text-zinc-700 dark:text-zinc-300">
                      {link.referralUrl}
                    </span>
                    <button
                      onClick={() => handleCopy(link)}
                      className="shrink-0 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                      title="Copy referral URL"
                    >
                      {copiedId === link.id ? (
                        <Check className="h-3.5 w-3.5 text-green-500" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </TableCell>
                <TableCell className="text-sm text-zinc-500">
                  {formatDate(link.createdAt)}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    plain
                    disabled={archiveMutation.isPending}
                    onClick={() => handleArchive(link)}
                    title="Archive link"
                  >
                    <Trash2 className="h-4 w-4 text-red-400" />
                  </Button>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </Section>
  );
}
